import EditorActions from 'components/Common/EditorActions'
import ProfileEditError from '../ProfileEditError'
import ProfileEditResult from '../ProfileEditResult'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { useFormik } from 'formik'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { get } from 'helpers/api_helper'
import EditorNavigation from '../EditorNavigation'
import useProfileEditOperation from '../useProfileEditOperation'
import AdminRoleGuide from './AdminRoleGuide'
import { canManageAdmins } from './adminDisplay'
import '../PlayerAccounts/playerCreation.scss'

const validate = values => {
  const errors = {}
  for (const name of ['firstName', 'lastName']) {
    if (!/^[\p{L}\p{M}][\p{L}\p{M} '\u2019-]*$/u.test(values[name].trim()) || values[name].trim().length > 100) errors[name] = 'Enter a name using letters, spaces, apostrophes or hyphens.'
  }
  if (values.email.trim().length > 254 || !/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(values.email.trim())) errors.email = 'Enter a valid email address.'
  if (!['admin', 'superAdmin'].includes(values.role)) errors.role = 'Choose an administrative role.'
  return errors
}
function AdminEditor({ account, userDetails, match, location, reload }) {
  const { profile } = account
  const id = match.params.id
  const command = useProfileEditOperation({ actorId: userDetails.id || userDetails._id, id, kind: 'admin' })
  const permitted = canManageAdmins(userDetails) && profile.status !== 'deleted'
  const self = id === String(userDetails.id || userDetails._id)
  const [submitted, setSubmitted] = useState(false)
  const [confirmation, setConfirmation] = useState(null)
  const form = useFormik({ initialValues: { firstName: profile.firstName || '', lastName: profile.lastName || '', email: profile.email || '', role: profile.role || '' }, validate,
    onSubmit: values => {
      if (!permitted || command.locked) return
      const next = { firstName: values.firstName.trim().normalize('NFC'), lastName: values.lastName.trim().normalize('NFC'),
        email: values.email.trim().normalize('NFC').toLowerCase(), role: values.role }
      const emailChanged = next.email !== profile.email?.toLowerCase()
      const promotes = next.role === 'superAdmin' && profile.role !== 'superAdmin'
      const payload = { profile: next, revision: account.revision, confirmations: {
        ...(emailChanged ? { email: next.email } : {}), ...(promotes ? { superAdmin: true } : {}),
      } }
      if (emailChanged || promotes) setConfirmation({ payload, emailChanged, promotes })
      else return command.save(payload)
    },
  })
  useEffect(() => setConfirmation(null), [form.values])
  const submit = async event => {
    event.preventDefault()
    if (!permitted || command.locked) return
    setSubmitted(true)
    const errors = await form.validateForm()
    if (Object.keys(errors).length) { document.getElementById('admin-edit-' + Object.keys(errors)[0])?.focus(); return }
    await form.submitForm()
  }
  const back = { pathname: '/accounts/admins/' + id, state: location.state }
  const field = (name, label, role = false) => {
    const error = (submitted || form.touched[name]) && form.errors[name]
    const input = { id: 'admin-edit-' + name, name, value: form.values[name], onChange: form.handleChange,
      onBlur: form.handleBlur, className: 'form-control' + (error ? ' is-invalid' : ''), required: true,
      'aria-invalid': Boolean(error), 'aria-describedby': error ? 'admin-edit-' + name + '-error' : undefined }
    return <div className="form-group"><label htmlFor={input.id}>{label} *</label>
      {role ? <select {...input} disabled={self}><option value="admin">Admin</option><option value="superAdmin">SuperAdmin</option></select>
        : <input {...input} type={name === 'email' ? 'email' : 'text'} />}
      {role && self && <small className="text-muted">You cannot change your own role.</small>}
      {error && <div className="invalid-feedback" id={input.id + '-error'}>{error}</div>}
    </div>
  }
  if (command.status === 'saved') return <ProfileEditResult command={command} back={back} label="Open admin profile" reload={reload} />
  return <>
    <EditorNavigation dirty={form.dirty} pending={command.locked} />
    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3"><div><h4>{profile.firstName} {profile.lastName}</h4>
      <span className="text-muted">#{profile.userId} · {profile.status}</span></div><Link className="btn btn-light" to={back}>Back to profile</Link></div>
    {!permitted && <p role="status" className="alert alert-info">{profile.status === 'deleted' ? 'Deleted accounts cannot be edited.' : 'Administrative accounts are read-only for Admins. An activated SuperAdmin can make changes.'}</p>}
    <Modal isOpen={Boolean(confirmation)} toggle={() => setConfirmation(null)}>
      <ModalHeader toggle={() => setConfirmation(null)}>{confirmation?.promotes ? 'Grant SuperAdmin access?' : 'Change sign-in email?'}</ModalHeader>
      <ModalBody>{confirmation?.promotes && <p>This account will be able to manage all Control Panel sections and other administrative accounts while activated.</p>}
        {confirmation?.emailChanged && <><p>{profile.email} → {confirmation.payload.profile.email}</p><p>The password and status are preserved. Unused invitation and reset links become invalid. No invitation is sent automatically.</p></>}
      </ModalBody><ModalFooter><button type="button" className="btn btn-light" onClick={() => setConfirmation(null)}>Keep editing</button>
        <button type="button" className="btn btn-primary" onClick={() => { const { payload } = confirmation; setConfirmation(null); command.save(payload) }}>Confirm and save</button></ModalFooter>
    </Modal>
    <form aria-label="Edit admin" onSubmit={submit} noValidate>
      <ProfileEditError command={command} reload={reload} />
    {command.locked && <div className="alert alert-info" role="status"><p>{command.status === 'unknown' ? 'Saving has not been confirmed. Check the same request before continuing.' : 'Waiting for confirmation…'}</p>
        {command.status === 'unknown' && <><button type="button" className="btn btn-light" onClick={command.check}>Check save status</button><button type="button" className="btn btn-light" onClick={command.resume}>Resume save</button></>}</div>}
      {submitted && Object.keys(form.errors).length > 0 && <p role="alert" className="alert alert-danger">Complete the highlighted required details before saving.</p>}
      <fieldset disabled={!permitted || command.locked}>
        <section className="card"><div className="card-body"><h5 className="mb-3">Account details</h5><div className="player-creation-grid">
          {field('firstName', 'First name')}{field('lastName', 'Last name')}{field('email', 'Email address')}
          <div><strong>Status: {profile.status}</strong><p className="text-muted">Status and deletion are managed from the profile. Editing a pending account does not activate it.</p></div>
        </div></div></section>
        <section className="card"><div className="card-body"><h5>Administrative access</h5>
          <div className="player-creation-grid">{field('role', 'Role', true)}</div><AdminRoleGuide />
        </div></section>
      </fieldset>
      <EditorActions dirty={form.dirty} pending={command.locked}>
        <Link className="btn btn-light" to={back}>Cancel</Link>{permitted && <button type="submit" className="btn btn-primary" disabled={command.locked}>Save changes</button>}
      </EditorActions>
    </form>
  </>
}
export function EditAdmin({ userDetails, ...props }) {
  const id = props.match.params.id
  const actorId = userDetails.id || userDetails._id
  const [account, setAccount] = useState(null)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let alive = true; setAccount(null); setError('')
    get('/admin/admin-profiles/' + id).then(response => {
      if (!response?.data?.profile || !response.data.revision) throw new Error('Unavailable admin')
      if (alive) setAccount(response.data)
    }).catch(() => { if (alive) setError('Could not load this administrative account.') })
    return () => { alive = false }
  }, [id, actorId, retry])
  return <div className="page-content"><Container fluid><Breadcrumbs title="Admins" path={{ pathname: '/accounts/admins', search: props.location?.state?.adminListSearch || '', state: props.location?.state }} breadcrumbItem="Edit admin" />
    <div className="player-creation">{error ? <div role="alert">{error} <button type="button" className="btn btn-primary" onClick={() => setRetry(value => value + 1)}>Retry admin</button></div>
      : !account ? <p role="status">Loading admin…</p>
        : <AdminEditor key={actorId + ':' + id + ':' + retry} {...props} account={account} userDetails={userDetails} reload={() => setRetry(value => value + 1)} />}</div>
  </Container></div>
}
export default function ConnectedEditAdmin(props) {
  const userDetails = useSelector(state => state.session.userDetails) || {}
  return <EditAdmin {...props} userDetails={userDetails} />
}
